import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button, Card } from '@pm/ui';
import type { PermissionKey } from '@pm/contracts';
import i18n from '../i18n.js';
import { api } from '../api/client.js';

type ProductGuide = {
  id: string;
  title: string;
  description: string;
  href: string;
  permission: PermissionKey;
  route: RegExp;
  steps: Array<{ id: string; title: string; body: string }>;
};

export const productGuides: ProductGuide[] = [
  {
    id: 'capacity-first-booking',
    title: 'Première réservation de capacité',
    description: 'Comprendre la charge officielle, les scénarios et les conflits.',
    href: '/capacity',
    permission: 'capacity.read',
    route: /^\/capacity|^\/projects\/[^/]+\/capacity/,
    steps: [
      {
        id: 'filters',
        title: 'Choisissez votre vue',
        body: 'Basculez entre Personnes, Équipes et Projets, puis réduisez la période si nécessaire.',
      },
      {
        id: 'scenario',
        title: 'Distinguez officiel et scénario',
        body: 'Une réservation officielle consomme la capacité. Un scénario provisoire est affiché brut et hachuré, sans pondération.',
      },
      {
        id: 'reserve',
        title: 'Réservez et vérifiez',
        body: 'Ouvrez une semaine, sélectionnez le projet et contrôlez le résumé des conflits avant validation.',
      },
    ],
  },
  {
    id: 'billing-review',
    title: 'Réviser et verrouiller la facturation',
    description: 'Passer de la revue PM au lot Finance sans envoi automatique.',
    href: '/billing',
    permission: 'billing.read',
    route: /^\/billing|^\/projects\/[^/]+\/billing/,
    steps: [
      {
        id: 'review',
        title: 'Filtrez la file PM',
        body: 'Sélectionnez les worklogs compatibles et fournissez un motif pour les statuts non approuvés.',
      },
      {
        id: 'blockers',
        title: 'Corrigez les blocages',
        body: 'Un compte, une expertise et un taux effectif sont requis avant la création du lot.',
      },
      {
        id: 'finance',
        title: 'Verrouillez côté Finance',
        body: 'Le verrou rend le lot stable. Aucun envoi QuickBooks automatique n’est effectué.',
      },
    ],
  },
  {
    id: 'report-publication',
    title: 'Publier un rapport client',
    description: 'Aperçu, baseline, approbation, PDF puis publication Scope.',
    href: '/reports',
    permission: 'reports.read',
    route: /^\/reports|^\/projects\/[^/]+\/reports/,
    steps: [
      {
        id: 'preview',
        title: 'Contrôlez l’aperçu',
        body: 'Seuls les éléments explicitement visibles client et publiables sont inclus.',
      },
      {
        id: 'baseline',
        title: 'Créez et approuvez la baseline',
        body: 'La baseline est immuable et son empreinte permet de vérifier le contenu approuvé.',
      },
      {
        id: 'publish',
        title: 'Publiez',
        body: 'Téléchargez le PDF ou publiez vers Scope après approbation.',
      },
    ],
  },
  {
    id: 'access-review',
    title: 'Faire une revue d’accès',
    description: 'Simuler les droits et identifier les affectations incomplètes.',
    href: '/admin/access',
    permission: 'admin.roles.read',
    route: /^\/admin\/access/,
    steps: [
      {
        id: 'select',
        title: 'Sélectionnez personne et projet',
        body: 'La simulation calcule les permissions effectives côté serveur.',
      },
      {
        id: 'sources',
        title: 'Vérifiez la provenance',
        body: 'Chaque permission indique son rôle global ou son équipe et rôle d’équipe.',
      },
      {
        id: 'export',
        title: 'Exportez la revue',
        body: 'Le CSV reprend uniquement le résultat autorisé de la simulation affichée.',
      },
    ],
  },
];

const guideProgressResponseSchema = z.object({
  data: z.object({
    guideId: z.string().min(1),
    stepId: z.string().nullable(),
    status: z.enum(['not_started', 'started', 'completed', 'abandoned']),
  }),
});

export function ContextualHelp() {
  const location = useLocation();
  const navigate = useNavigate();
  const guide = productGuides.find((candidate) => candidate.route.test(location.pathname));
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogHeadingRef = useRef<HTMLHeadingElement>(null);
  const progress = useQuery({
    queryKey: ['guide-progress', guide?.id],
    queryFn: () => api(`/api/v1/workspace/guides/${guide!.id}`, {}, guideProgressResponseSchema),
    enabled: Boolean(guide),
  });

  const sendEvent = useCallback(
    async (
      name: 'guide_started' | 'step_completed' | 'guide_completed' | 'guide_abandoned',
      stepId: string | null,
    ) => {
      await api('/api/v1/workspace/telemetry/events', {
        method: 'POST',
        body: JSON.stringify({
          name,
          guideId: guide!.id,
          stepId,
          route: location.pathname,
          locale: i18n.language.startsWith('en') ? 'en' : 'fr',
        }),
      });
    },
    [guide, location.pathname],
  );
  const openGuide = () => {
    const resumeIndex = guide!.steps.findIndex((step) => step.id === progress.data?.data.stepId);
    setStepIndex(Math.max(0, resumeIndex));
    setOpen(true);
    void sendEvent('guide_started', guide!.steps[Math.max(0, resumeIndex)]?.id ?? null);
  };
  const closeGuide = useCallback(() => {
    setOpen(false);
    if (progress.data?.data.status !== 'completed') {
      void sendEvent('guide_abandoned', guide!.steps[stepIndex]?.id ?? null);
    }
  }, [guide, progress.data?.data.status, sendEvent, stepIndex]);
  useEffect(() => {
    if (!open) return;
    dialogHeadingRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeGuide();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeGuide, open]);
  useEffect(() => {
    if (!open) triggerRef.current?.focus();
  }, [open]);
  if (!guide) {
    return (
      <button
        ref={triggerRef}
        className="context-help-button"
        type="button"
        aria-label="Ouvrir le centre d’aide"
        title="Centre d’aide"
        onClick={() => void navigate('/help')}
      >
        ?
      </button>
    );
  }
  const next = () => {
    const step = guide.steps[stepIndex];
    if (!step) return;
    void sendEvent('step_completed', step.id);
    if (stepIndex === guide.steps.length - 1) {
      void sendEvent('guide_completed', step.id);
      setOpen(false);
      void progress.refetch();
      return;
    }
    setStepIndex((current) => current + 1);
  };
  return (
    <>
      <button
        ref={triggerRef}
        className="context-help-button"
        type="button"
        aria-label={
          progress.data?.data.status === 'started'
            ? 'Reprendre le guide contextuel'
            : 'Ouvrir le guide contextuel'
        }
        title={
          progress.data?.data.status === 'started'
            ? 'Reprendre le guide contextuel'
            : 'Guide contextuel'
        }
        onClick={openGuide}
      >
        ?
      </button>
      {open &&
        createPortal(
          <div className="modal-backdrop" role="presentation" onMouseDown={closeGuide}>
            <Card
              className="context-help-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="context-help-title"
              aria-describedby="context-help-body"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <small>
                Étape {stepIndex + 1}/{guide.steps.length}
              </small>
              <h2 id="context-help-title" ref={dialogHeadingRef} tabIndex={-1}>
                {guide.steps[stepIndex]?.title}
              </h2>
              <p id="context-help-body">{guide.steps[stepIndex]?.body}</p>
              <div className="button-row">
                {stepIndex > 0 && (
                  <Button onClick={() => setStepIndex((current) => current - 1)}>Précédent</Button>
                )}
                <Button onClick={next}>
                  {stepIndex === guide.steps.length - 1 ? 'Terminer' : 'Suivant'}
                </Button>
                <Button onClick={closeGuide}>Fermer</Button>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    setOpen(false);
                    void navigate('/help');
                  }}
                >
                  Tous les guides
                </button>
              </div>
            </Card>
          </div>,
          document.body,
        )}
    </>
  );
}
